import { useCallback, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { tokens } from '@fluentui/tokens'
import { makeStyles, mergeClasses } from '@griffel/react'
import { Text } from '@fluentui/react-text'
import { List as WindowList, RowComponentProps } from 'react-window'
import { AutoSizer } from 'react-virtualized-auto-sizer'
import { GenericList, useGenericListSettings } from '@knowmax/genericlist-core'
import { Loader, ErrorMessage } from '../../components/core'
import { LANGUAGE_LABELNORESULTS } from '../../types/language'
import { TOnRenderListItem, TListLayout } from './List'

/**
 * Default item heights used when `itemHeight` is not provided, keyed by layout.
 * `compact` gets a smaller row since it renders denser content. `grid` and `card`
 * fall back to the same height as `list` because react-window uses absolute-positioned
 * rows – CSS multi-column grid on the container has no effect on the children.
 */
const ITEM_HEIGHT_BY_LAYOUT: Record<TListLayout, number> = {
  list: 90,
  compact: 64,
  grid: 90,
  card: 90,
}

export interface IVirtualizedListProps<T = unknown> {
  list: GenericList<T>
  onRenderListItem: TOnRenderListItem<T>
  /**
   * Fixed pixel height of each list item row.
   * When omitted, defaults to 90 px for `list`/`grid`/`card` and 64 px for `compact`.
   */
  itemHeight?: number
  /**
   * Height of the scrollable list container in pixels.
   *
   * When omitted, the list expands to fill all available vertical space in its
   * parent using `AutoSizer` (from `react-virtualized-auto-sizer`). This requires
   * that a parent element in the tree has a **constrained height** (e.g. `height:
   * 100vh`, `flex: 1` in a fixed-height flex column, or an explicit pixel value).
   * Without a constrained ancestor the measured height will be 0 and nothing will
   * render.
   *
   * When provided, `AutoSizer` is skipped and the list container is sized to the
   * exact pixel value – useful when the parent height is unconstrained.
   */
  height?: number
  /**
   * Layout style – mirrors the `layout` prop of `List` to ease migration.
   *
   * `list` and `compact` translate naturally to row-based virtualization.
   * `grid` and `card` are accepted for API compatibility but items will still
   * render one per row – react-window's absolute-positioned rows are incompatible
   * with CSS multi-column grid on the container.
   */
  layout?: TListLayout
  /** Number of extra items rendered outside the visible area for smoother scrolling. Defaults to 3. */
  overscanCount?: number
  className?: string
}

// ── Internal row data ─────────────────────────────────────────────────────────

type RowData = {
  items: unknown[]
  onRenderListItem: TOnRenderListItem<unknown>
}

/**
 * Row renderer for react-window. Defined outside the observer component so its
 * reference stays stable across re-renders (react-window relies on referential
 * stability for the rowComponent prop to avoid remounting rows unnecessarily).
 */
const RowRenderer = ({ index, style, ariaAttributes, items, onRenderListItem }: RowComponentProps<RowData>) => {
  const item = items[index]
  return (
    <div style={style} {...ariaAttributes}>
      {item !== undefined && onRenderListItem(item, index)}
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const useClasses = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS
  },
  listContainer: {
    flex: '1 1 auto',
    overflow: 'hidden'
  },
  loadingFooter: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalS
  }
})

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Virtualized list component that renders items with react-window and loads more
 * results automatically as the user scrolls down (infinite scroll).
 *
 * Items from the underlying {@link GenericList} are accumulated across pages so the
 * user never loses previously loaded rows. A new page is requested from the server
 * once the visible window reaches within {@link IVirtualizedListProps.overscanCount}
 * rows of the last loaded item.
 *
 * **Height behaviour**
 *
 * - When `height` is **omitted** the list uses `AutoSizer` to fill all available
 *   vertical space in its parent. The parent (or an ancestor) must have a
 *   constrained height; otherwise the measured size will be 0 and nothing renders.
 * - When `height` is **provided** `AutoSizer` is bypassed and the container is
 *   fixed to that pixel value, which works fine even when the parent height is
 *   unconstrained.
 *
 * The existing {@link List} component remains unchanged and continues to work with
 * a traditional pager – this component is an additive, non-breaking addition.
 */
export const VirtualizedList = observer(<T,>({
  list,
  onRenderListItem,
  itemHeight,
  height,
  layout = 'list',
  overscanCount = 3,
  className
}: IVirtualizedListProps<T>) => {
  const effectiveItemHeight = itemHeight ?? ITEM_HEIGHT_BY_LAYOUT[layout]
  const classes = useClasses()
  const settings = useGenericListSettings()

  /** All items accumulated across all loaded pages. */
  const [allItems, setAllItems] = useState<T[]>([])

  /** Tracks which page we last merged into allItems. */
  const processedPageRef = useRef(0)

  /**
   * Config hash: captures filter, order, and pageSize – anything that changes
   * the result set (but not the page number itself).  When this changes we reset
   * the accumulated items and reload from page 1.
   */
  const configHash = `${list.filter ?? ''}|${list.orderExpression}|${list.pageSize}`

  /** Guard against dispatching multiple concurrent load-more requests. */
  const isRequestingMoreRef = useRef(false)

  // ── Initial load & config-change reset ────────────────────────────────────
  useEffect(() => {
    list.setPage(1)
    processedPageRef.current = 0
    setAllItems([])
    isRequestingMoreRef.current = false
    list.load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, configHash])

  // ── Accumulate items when a page finishes loading ──────────────────────────
  useEffect(() => {
    if (!list.loaded || list.loading) return

    const incomingPage = list.page

    if (incomingPage === 1) {
      // Handles: initial load, refresh (RefreshList calls setPage(1) before reloading),
      // and save/delete when we haven't yet scrolled past page 1.
      setAllItems(list.list.slice() as T[])
      processedPageRef.current = 1
    } else if (incomingPage > processedPageRef.current) {
      // Normal infinite scroll: a new page arrived, append its rows.
      setAllItems(prev => [...prev, ...(list.list.slice() as T[])])
      processedPageRef.current = incomingPage
    } else {
      // An already-accumulated page was reloaded without resetting to page 1
      // (save/delete calls list.load(true) at the current page).
      // Reset back to page 1 so the accumulated data stays consistent.
      processedPageRef.current = 0
      isRequestingMoreRef.current = false
      setAllItems([])
      list.setPage(1)
      list.load()
      return
    }

    isRequestingMoreRef.current = false
  // list.loading / list.page / list.loaded are plain MobX observable primitives
  // (boolean / number) so React's Object.is comparison works correctly here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.loading, list.page, list.loaded])

  // ── Load-more trigger ──────────────────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (list.loading || isRequestingMoreRef.current || allItems.length >= list.totalCount) return

    isRequestingMoreRef.current = true
    list.setPage(list.page + 1)
    list.load()
  }, [list, allItems.length])

  /**
   * Called by react-window after each render pass.  We trigger a load-more when
   * the last fully-rendered row is within `overscanCount` of the end of the
   * currently loaded items.
   */
  const handleRowsRendered = useCallback(
    (_visibleRows: { startIndex: number; stopIndex: number }, allRows: { startIndex: number; stopIndex: number }) => {
      if (allRows.stopIndex >= allItems.length - 1 - overscanCount) {
        loadMore()
      }
    },
    [allItems.length, overscanCount, loadMore]
  )

  const hasItems = allItems.length > 0
  const isInitialLoading = (!list.loaded || allItems.length === 0) && list.loading
  const isEmpty = list.loaded && !list.loading && allItems.length === 0
  const isLoadingMore = list.loading && hasItems

  // rowProps must satisfy ExcludeForbiddenKeys_2<RowData>; "index" / "style" /
  // "ariaAttributes" are excluded from rowProps (react-window injects them itself).
  const rowProps: RowData = {
    items: allItems as unknown[],
    onRenderListItem: onRenderListItem as TOnRenderListItem<unknown>
  }

  return (
    <div className={mergeClasses(classes.root, className)}>
      {list.error && <ErrorMessage error={list.error} />}

      {isInitialLoading && <Loader />}

      {isEmpty && (
        <Text>{settings.onTranslate(LANGUAGE_LABELNORESULTS)}</Text>
      )}

      {hasItems && (
        <div style={height !== undefined ? { height } : undefined} className={classes.listContainer}>
          {height !== undefined ? (
            <WindowList
              rowComponent={RowRenderer}
              rowCount={allItems.length}
              rowHeight={effectiveItemHeight}
              rowProps={rowProps}
              overscanCount={overscanCount}
              onRowsRendered={handleRowsRendered}
              style={{ height: '100%', width: '100%' }}
            />
          ) : (
            <AutoSizer
              renderProp={({ height: autoHeight, width }: { height: number | undefined; width: number | undefined }) => (
                <WindowList
                  rowComponent={RowRenderer}
                  rowCount={allItems.length}
                  rowHeight={effectiveItemHeight}
                  rowProps={rowProps}
                  overscanCount={overscanCount}
                  onRowsRendered={handleRowsRendered}
                  style={{ height: autoHeight ?? '100%', width: width ?? '100%' }}
                />
              )}
            />
          )}
        </div>
      )}

      {isLoadingMore && (
        <div className={classes.loadingFooter}>
          <Loader size='sm' />
        </div>
      )}
    </div>
  )
})
